import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { getSuperAdminClaim } from '@confluence/super-admin-claim';
import { expVal } from '@confluence/feature-experiments';

import type { SpaceRestrictionCheckQuery as SpaceRestrictionCheckQueryType } from './__types__/SpaceRestrictionCheckQuery.ts';
import { SpaceRestrictionCheckQuery } from './SpaceRestrictionCheckQuery.graphql.ts';

type SpaceRestrictionCheckProps = {
	asSuperAdmin?: boolean;
	children: (result: {
		error?: ApolloErrorInterop;
		isSpaceNotFound: boolean | undefined;
		isSpaceRestricted: boolean | undefined;
		loading?: boolean;
	}) => JSX.Element;
	spaceKey: string;
	contentId?: string;
};

export function SpaceRestrictionCheck({
	asSuperAdmin,
	children,
	spaceKey,
	contentId,
}: SpaceRestrictionCheckProps) {
	const superAdminClaim = getSuperAdminClaim();
	const [stateSpaceKey] = usePageSpaceKey();

	let data, error, loading;
	if (expVal('confluence_restrictions_fix', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const result = useQuery<SpaceRestrictionCheckQueryType>(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations
			SpaceRestrictionCheckQuery,
			{
				context: asSuperAdmin == undefined ? undefined : { asSuperAdmin },
				errorPolicy: 'all',
				variables: {
					// XXX Though SpaceRestrictionCheckProps defines spaceKey as required,
					// SpaceBaseComponent is not written in TypeScript and it may provide
					// undefined as it is reading spaceKey from redux. While it would result
					// in a better separation of concerns to have a fix in SpaceBaseComponent,
					// such a fix would be riskier.
					spaceKey: spaceKey || stateSpaceKey!,
				},
			},
		);
		data = result.data;
		error = result.error;
		loading = result.loading;
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const result = useQuery<SpaceRestrictionCheckQueryType>(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations
			SpaceRestrictionCheckQuery,
			{
				context: asSuperAdmin == undefined ? undefined : { asSuperAdmin },
				fetchPolicy: superAdminClaim ? 'no-cache' : 'network-only',
				errorPolicy: 'all',
				variables: {
					// XXX Though SpaceRestrictionCheckProps defines spaceKey as required,
					// SpaceBaseComponent is not written in TypeScript and it may provide
					// undefined as it is reading spaceKey from redux. While it would result
					// in a better separation of concerns to have a fix in SpaceBaseComponent,
					// such a fix would be riskier.
					spaceKey: spaceKey || stateSpaceKey!,
				},
			},
		);
		data = result.data;
		error = result.error;
		loading = result.loading;
	}

	let isErrorHandled: boolean | undefined;
	let isSpaceNotFound: boolean | undefined;
	let isSpaceRestricted: boolean | undefined;

	if (error) {
		isSpaceNotFound = isSpaceNotFoundError(error);
		isSpaceRestricted = isSpaceRestrictedError(error);
		if (isSpaceNotFound || isSpaceRestricted) {
			markErrorAsHandled(error);
			isErrorHandled = true;
		}
	} else if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR &&
		expVal('confluence_frontend_ssr_space_not_found', 'isEnabled', false)
	) {
		isSpaceNotFound = window['__SPACE_NOTFOUND_CHECK_ERROR__'];
		isSpaceRestricted = window['__SPACE_RESTRICTED_CHECK_ERROR__'];
		isErrorHandled =
			window['__SPACE_RESTRICTED_CHECK_ERROR__'] || window['__SPACE_NOTFOUND_CHECK_ERROR__'];
	} else if (data) {
		const errorSwallowed = data.space === null;
		isSpaceNotFound = errorSwallowed && window['__SPACE_NOTFOUND_CHECK_ERROR__'];
		isSpaceRestricted = errorSwallowed && window['__SPACE_RESTRICTED_CHECK_ERROR__'];
	}

	if (expVal('confluence_restrictions_fix', 'isEnabled', false)) {
		if (loading) {
			return null;
		}

		const contentIsNotViewableBecauseOfSpaceRestrictions =
			Boolean(contentId) && window?.['__SPACE_RESTRICTED_ERROR__'] === contentId;

		return children({
			error,
			isSpaceNotFound,
			isSpaceRestricted: isSpaceRestricted || contentIsNotViewableBecauseOfSpaceRestrictions,
		});
	} else {
		return children({
			error: isErrorHandled ? undefined : error,
			isSpaceNotFound,
			isSpaceRestricted,
			loading,
		});
	}
}

export function isSpaceNotFoundError(error: ApolloErrorInterop) {
	return isSpaceError(error, ({ statusCode }) => statusCode === 404 || statusCode === 400);
}

export function isSpaceRestrictedError(error: ApolloErrorInterop) {
	return isSpaceError(error, () => isUnauthorizedError(error));
}

function isSpaceError(
	{ graphQLErrors }: ApolloErrorInterop,
	extensionsPredicate: (extensions: {
		data?: { authorized?: boolean };
		statusCode?: number;
	}) => boolean,
) {
	if (graphQLErrors && graphQLErrors.length > 0) {
		for (const { extensions, path } of graphQLErrors) {
			if (
				Array.isArray(path) &&
				path.indexOf('space') !== -1 &&
				extensions &&
				extensionsPredicate(extensions)
			) {
				return true;
			}
		}
	}
	return false;
}
